import { getAppConfig } from '@shared/app-config'

/**
 * All API route templates, grouped by domain.
 * Dynamic path segments use `{paramName}` placeholders (OpenAPI-style).
 * Each leaf value is a flat, fully-formed route — no concatenation needed at call site.
 *
 * Internally, routes use `@/…` shorthand that resolves against the group's `@prefix`.
 * Resolution happens once inside `getApiRoutes()`, so consumers always receive clean strings.
 *
 * Base URL resolution:
 * - HTTP routes: `getEnv().APP_API_URL` + route  (handled by axios baseURL)
 * - WebSocket:   `getEnv().APP_WEBSOCKET_URL` + `getApiRoutes().ws.join`  (composed in ws/client.ts)
 */
export interface ApiRoutes {
  agents: {
    list: string
    details: string
    byName: string
  }
  sessions: {
    list: string
    get: string
    messages: string
  }
  models: {
    list: string
    byName: string
  }
  uploads: {
    list: string
    get: string
    create: string
  }
  artifacts: {
    get: string
    content: string
    state: string
    baseUrl: string
  }
  pages: {
    list: string
    routes: string
    get: string
    content: string
    baseUrl: string
  }
  registry: {
    namespaces: string
    entries: string
    entry: string
    entryCreate: string
    entryEditors: string
    versions: string
    versionApply: string
  }
  keeperSync: {
    state: string
    download: string
    upload: string
    undo: string
    redo: string
  }
  components: {
    list: string
    byTag: string
  }
  tools: {
    byName: string
  }
  ws: {
    join: string
  }
}

/** Internal type — every group carries a `@prefix` used for `@/…` shorthand resolution. */
type ApiRoutesRaw = {
  [K in keyof ApiRoutes]: ApiRoutes[K] & { '@prefix': string }
}

/**
 * Override type for `AppConfig.feature.apiRoutes`.
 *
 * Each group is optional and partially overridable.
 * Set `@prefix` to rebase all `@/…` routes in that group at once,
 * or override individual routes with absolute paths.
 */
export type ApiRoutesOverride = {
  [K in keyof ApiRoutes]?: Partial<ApiRoutes[K]> & { '@prefix'?: string }
}

const defaultApiRoutes: ApiRoutesRaw = {
  agents: {
    '@prefix': '/api/v1/agents',
    'list': '@/list',
    'details': '@/details?agent={agent}',
    'byName': '@/by-name',
  },
  sessions: {
    '@prefix': '/api/v1/sessions',
    'list': '@',
    'get': '@/get?session_id={session_id}',
    'messages': '@/messages?session_id={session_id}',
  },
  models: {
    '@prefix': '/api/v1/models',
    'list': '@/list',
    'byName': '@/by-name',
  },
  uploads: {
    '@prefix': '/api/v1/uploads',
    'list': '@/list',
    'get': '@/{uuid}',
    'create': '@',
  },
  artifacts: {
    '@prefix': '/api/v1/artifact',
    'get': '@/{uuid}',
    'content': '@/{uuid}/content',
    'state': '@/{artifactId}/state',
    'baseUrl': '@/{uuid}/public',
  },
  pages: {
    '@prefix': 'api/public/pages',
    'list': '@/list',
    'routes': '@/routes',
    'get': '@/{pageId}',
    'content': '@/content/{pageId}',
    'baseUrl': '@/public/{pageId}',
  },
  registry: {
    '@prefix': '/api/v1/keeper/registry',
    'namespaces': '@/namespaces',
    'entries': '@/entries',
    'entry': '@/entry?id={id}',
    'entryCreate': '@/entry',
    'entryEditors': '@/entry/editors?id={id}',
    'versions': '@/versions',
    'versionApply': '@/version/apply',
  },
  keeperSync: {
    '@prefix': '/api/v1/keeper/sync',
    'state': '@/state',
    'download': '@/download',
    'upload': '@/upload',
    'undo': '@/undo',
    'redo': '@/redo',
  },
  components: {
    '@prefix': 'api/public/components',
    'list': '@/list',
    'byTag': '@/by-tag/{tag}',
  },
  tools: {
    '@prefix': '/api/v1/tools',
    'byName': '@/by-name',
  },
  ws: {
    '@prefix': '/api/v1/ws',
    'join': '@/join?x-auth-token={token}',
  },
}

/**
 * Resolves `@/…` shorthand in a single group against its `@prefix`.
 * - `'@'`       → the prefix itself
 * - `'@/path'`  → prefix + `/path`
 * - other       → kept as-is (absolute path)
 */
function resolveGroup(group: Record<string, string>): Record<string, string> {
  const prefix = group['@prefix'] || ''
  const resolved: Record<string, string> = {}
  for (const [key, value] of Object.entries(group)) {
    if (key === '@prefix')
      continue
    if (value === '@') {
      resolved[key] = prefix
    }
    else if (value.startsWith('@/')) {
      resolved[key] = prefix + value.slice(1) // keep the '/' after '@'
    }
    else {
      resolved[key] = value
    }
  }
  return resolved
}

/** Eagerly resolved default routes (no overrides). */
const resolvedDefaults: ApiRoutes = (() => {
  const result = {} as ApiRoutes
  for (const key of Object.keys(defaultApiRoutes) as Array<keyof ApiRoutes>) {
    result[key] = resolveGroup(defaultApiRoutes[key] as unknown as Record<string, string>) as any
  }
  return result
})()

/**
 * Returns fully-resolved API routes (defaults + AppConfig overrides).
 * The `@prefix` / `@/…` shorthand is resolved here — consumers receive clean strings.
 * Call at runtime, not at import time.
 */
export function getApiRoutes(): ApiRoutes {
  const overrides = getAppConfig()?.apiRoutes
  if (!overrides)
    return resolvedDefaults

  const result = {} as ApiRoutes
  for (const key of Object.keys(defaultApiRoutes) as Array<keyof ApiRoutes>) {
    if (overrides[key]) {
      const merged = { ...defaultApiRoutes[key], ...overrides[key] }
      result[key] = resolveGroup(merged as Record<string, string>) as any
    }
    else {
      result[key] = resolvedDefaults[key] as any
    }
  }
  return result
}

/**
 * Replaces `{paramName}` placeholders in a route template with actual values.
 * Values in the query-string part (after `?`) are automatically URI-encoded.
 */
export function resolveRoute(route: string, params: Record<string, string>): string {
  return Object.entries(params).reduce(
    (r, [key, value]) => {
      const placeholder = `{${key}}`
      const idx = r.indexOf(placeholder)
      if (idx === -1)
        return r
      const qIdx = r.indexOf('?')
      const encoded = (qIdx !== -1 && idx > qIdx) ? encodeURIComponent(value) : value
      return r.replace(placeholder, encoded)
    },
    route,
  )
}
